import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, DownloadSimple, FileText, BookOpen,
  CheckSquare, Sparkle, MagnifyingGlass, Clock,
  ArrowUpRight, Heart, Shield, Users, GraduationCap,
  Lock
} from "@phosphor-icons/react";
import DownloadModal from "../components/DownloadModal/index.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourcesPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedResource, setSelectedResource] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Trigger actual file download after form submission
  const handleDownloadSuccess = (resource) => {
    // Trigger the actual PDF download
    const downloadLink = document.createElement('a');
    downloadLink.href = `${API}/resources/download/${resource.resourceId}`;
    downloadLink.target = '_blank';
    downloadLink.click();
  };

  const categories = [
    { id: "all", label: "All Resources", count: 12 },
    { id: "guides", label: "Guides", count: 4 },
    { id: "checklists", label: "Checklists", count: 3 },
    { id: "whitepapers", label: "Whitepapers", count: 2 },
    { id: "blog", label: "Blog Articles", count: 3 },
  ];

  const featuredResources = [
    {
      id: 1,
      resourceId: "cms-cops-survey-guide-2025",
      type: "guide",
      title: "2025-2026 CMS CoPs Survey Readiness Guide",
      description: "Comprehensive guide covering the latest CMS Conditions of Participation requirements, including the new HOPE tool implementation effective October 2025, updated POC documentation standards, and face-to-face encounter requirements.",
      image: "https://images.unsplash.com/photo-1692158961562-cb06d93fb63c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwY29tcGxpYW5jZSUyMGNoZWNrbGlzdCUyMGRvY3VtZW50fGVufDB8fHx8MTc3MzAzMTQ3OXww&ixlib=rb-4.1.0&q=85",
      downloadCount: 4287,
      readTime: "35 min read",
      featured: true
    },
    {
      id: 2,
      resourceId: "chap-accreditation-checklist",
      type: "checklist",
      title: "CHAP Accreditation Standards Checklist v3.1.0",
      description: "Complete checklist aligned with CHAP Hospice Standards of Excellence v3.1.0 (effective June 2025). Covers patient-centered care, QAPI requirements, interdisciplinary coordination, and compliance program standards.",
      image: "https://images.unsplash.com/photo-1659353888477-6e6aab941b55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwY29tcGxpYW5jZSUyMGNoZWNrbGlzdCUyMGRvY3VtZW50fGVufDB8fHx8MTc3MzAzMTQ3OXww&ixlib=rb-4.1.0&q=85",
      downloadCount: 3156,
      readTime: "15 min read",
      featured: true
    },
    {
      id: 3,
      resourceId: "hope-tool-implementation",
      type: "whitepaper",
      title: "HOPE Tool Implementation: A Strategic Guide",
      description: "Deep dive into the Hospice Outcomes and Patient Evaluation (HOPE) tool replacing HIS. Includes submission requirements, new quality measures for pain/dyspnea management, and avoiding the 4% Medicare penalty.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZCUyMHRlY2hub2xvZ3klMjBibHVlfGVufDB8fHx8MTc3MzAzMTQ3MHww&ixlib=rb-4.1.0&q=85",
      downloadCount: 2890,
      readTime: "25 min read",
      featured: true
    }
  ];

  const resources = [
    {
      id: 4,
      resourceId: "qapi-program-development",
      type: "guide",
      title: "QAPI Program Development: CMS CoP §418.58 Compliance",
      description: "Step-by-step guide to building a QAPI program demonstrating measurable improvements in palliative outcomes. Includes audit templates, action plan frameworks, and performance indicators aligned with NHPCO standards.",
      icon: Shield,
      tags: ["QAPI", "CMS CoPs", "Quality Improvement"],
      readTime: "30 min read",
      downloadCount: 2134
    },
    {
      id: 5,
      resourceId: "poc-documentation-checklist",
      type: "checklist",
      title: "Plan of Care (POC) Documentation Checklist",
      description: "Ensure POC establishment within 48 hours per §418.56. Covers IDG signatures, measurable outcomes, functional status, psychosocial/spiritual support goals, and service frequency documentation.",
      icon: CheckSquare,
      tags: ["Documentation", "POC", "IDG"],
      readTime: "12 min read",
      downloadCount: 3567
    },
    {
      id: 6,
      resourceId: "census-growth-playbook",
      type: "guide",
      title: "Census Growth & Referral Development Playbook",
      description: "Data-driven strategies for hospice census growth including market analysis, referral source optimization, admission conversion tactics, and live discharge rate reduction methodologies.",
      icon: Users,
      tags: ["Census Growth", "Marketing", "Referrals"],
      readTime: "28 min read",
      downloadCount: 1876
    },
    {
      id: 7,
      resourceId: "idg-meeting-checklist",
      type: "checklist",
      title: "IDG Meeting Best Practices & Compliance Checklist",
      description: "Ensure productive and compliant Interdisciplinary Group meetings with this CMS CoP-aligned checklist. Includes agenda template, care conference documentation, and POC revision requirements.",
      icon: Users,
      tags: ["IDG", "Care Planning", "Team Meetings"],
      readTime: "10 min read",
      downloadCount: 2543
    },
    {
      id: 8,
      resourceId: "nhpco-compliance-overview",
      type: "whitepaper",
      title: "NHPCO Compliance Certificate Program Overview",
      description: "Analysis of NHPCO's 2024-2025 compliance program curriculum covering risk assessments, policy development, Medicare CoP alignment, and building a culture of compliance.",
      icon: Heart,
      tags: ["NHPCO", "Compliance", "Certification"],
      readTime: "18 min read",
      downloadCount: 1456
    },
    {
      id: 9,
      resourceId: "new-leader-onboarding",
      type: "guide",
      title: "New Leader Onboarding: Administrator & DON Handbook",
      description: "Comprehensive guide for Administrators, Directors of Clinical Services, and Compliance Officers transitioning into hospice leadership. Covers CMS regulatory awareness, team accountability, and operational excellence.",
      icon: GraduationCap,
      tags: ["Leadership", "Onboarding", "Management"],
      readTime: "40 min read",
      downloadCount: 1234
    }
  ];

  const blogPosts = [
    {
      id: 10,
      type: "blog",
      title: "CHAP Top 10 Hospice Deficiencies 2025: Prevention Strategies",
      description: "Analysis of CHAP's most frequently cited survey deficiencies including IDG/POC development issues, hospice aide supervision gaps, and discharge documentation errors with actionable prevention strategies.",
      author: "Dr. Elizabeth Noble",
      date: "January 28, 2026",
      readTime: "12 min read",
      tags: ["CHAP", "Survey", "Deficiencies"]
    },
    {
      id: 11,
      type: "blog",
      title: "FY 2026 Hospice Final Rule: What You Need to Know",
      description: "Breaking down the August 2025 CMS Final Rule including the 2.6% wage index increase, POC alignment updates, face-to-face encounter flexibilities, and HOPE implementation requirements.",
      author: "James Richardson",
      date: "January 15, 2026",
      readTime: "10 min read",
      tags: ["CMS", "Regulations", "Medicare"]
    },
    {
      id: 12,
      type: "blog",
      title: "Building a Person-Centered Care Culture in Hospice",
      description: "How to align your organization with 2026 survey focus areas: person-centered planning, quality outcomes, symptom management, safe transitions, and documentation linking assessments to interventions.",
      author: "Michelle Torres, RN",
      date: "January 5, 2026",
      readTime: "8 min read",
      tags: ["Culture", "Patient Care", "Leadership"]
    }
  ];

  const allResources = [...resources, ...blogPosts];

  const filteredResources = allResources.filter(resource => {
    const matchesCategory = activeCategory === "all" || 
      (activeCategory === "guides" && resource.type === "guide") ||
      (activeCategory === "checklists" && resource.type === "checklist") ||
      (activeCategory === "whitepapers" && resource.type === "whitepaper") ||
      (activeCategory === "blog" && resource.type === "blog");
    
    const matchesSearch = searchQuery === "" || 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const getTypeIcon = (type) => {
    switch(type) {
      case "guide": return FileText;
      case "checklist": return CheckSquare;
      case "whitepaper": return BookOpen;
      case "blog": return BookOpen;
      default: return FileText;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case "guide": return "bg-blue-500/10 text-blue-600";
      case "checklist": return "bg-green-500/10 text-green-600";
      case "whitepaper": return "bg-purple-500/10 text-purple-600";
      case "blog": return "bg-accent/10 text-accent";
      default: return "bg-primary/10 text-primary";
    }
  };

  const handleDownloadClick = (resource) => {
    setSelectedResource(resource);
    setIsModalOpen(true);
  };

  return (
    <div data-testid="resources-page">
      {/* Download Modal */}
      <DownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        resource={selectedResource}
        onSuccess={handleDownloadSuccess}
      />

      {/* Hero Section */}
      <section className="section-padding bg-hero-gradient relative overflow-hidden" data-testid="resources-hero">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="decorative-blob w-72 h-72 bg-accent/20 bottom-0 left-0" />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-primary mb-6">
              <BookOpen weight="fill" className="w-4 h-4 mr-2" />
              Resources & Insights
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
              Free Resources to{" "}
              <span className="text-gradient">Elevate Your Hospice</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Download guides, checklists, and whitepapers created by hospice experts. 
              Stay informed with our latest blog articles on compliance, leadership, and growth.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <MagnifyingGlass className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 pl-14 pr-6 bg-white border-2 border-border rounded-full text-foreground focus:outline-none focus:border-primary transition-colors shadow-lg"
                data-testid="resources-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="py-16 -mt-8 relative z-10" data-testid="featured-resources">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-xl border border-border hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`featured-resource-${resource.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={resource.image} 
                    alt={resource.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(resource.type)}`}>
                      {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-foreground flex items-center gap-1">
                      <Lock weight="bold" className="w-3 h-3" />
                      Free
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-white/80 text-sm flex items-center gap-2">
                      <DownloadSimple weight="bold" className="w-4 h-4" />
                      {resource.downloadCount.toLocaleString()} downloads
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-foreground mb-2 group-hover:text-primary transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {resource.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock weight="bold" className="w-3 h-3" />
                      {resource.readTime}
                    </span>
                    <button 
                      onClick={() => handleDownloadClick(resource)}
                      className="flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                      data-testid={`download-featured-${resource.id}`}
                    >
                      Get Free Access
                      <DownloadSimple weight="bold" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-y border-border bg-muted/30" data-testid="category-filter">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-muted-foreground hover:text-foreground border border-border hover:border-primary"
                }`}
                data-testid={`category-${category.id}`}
              >
                {category.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeCategory === category.id ? "bg-white/20" : "bg-muted"
                }`}>
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-padding" data-testid="resources-grid">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource, index) => {
              const TypeIcon = resource.icon || getTypeIcon(resource.type);
              return (
                <motion.div
                  key={resource.id}
                  className="bg-white border border-border rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`resource-${resource.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getTypeColor(resource.type)}`}>
                      <TypeIcon weight="duotone" className="w-6 h-6" />
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
                      {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                    </span>
                  </div>
                  
                  <h3 className="font-bold text-lg text-foreground mb-2 group-hover:text-primary transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {resource.description}
                  </p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {resource.tags?.map((tag, i) => (
                      <span key={i} className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    {resource.type === "blog" ? (
                      <>
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{resource.author}</span>
                          <span className="mx-2">•</span>
                          {resource.date}
                        </div>
                        <button className="flex items-center gap-1 text-primary font-semibold text-sm">
                          Read
                          <ArrowUpRight weight="bold" className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <DownloadSimple weight="bold" className="w-3 h-3" />
                          {resource.downloadCount?.toLocaleString()} downloads
                        </span>
                        <button 
                          onClick={() => handleDownloadClick(resource)}
                          className="flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                          data-testid={`download-${resource.id}`}
                        >
                          <Lock weight="bold" className="w-3 h-3" />
                          Get Access
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-16">
              <MagnifyingGlass className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No resources found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-muted/50" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkle weight="fill" className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              Get Weekly Insights Delivered
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join 5,000+ hospice leaders receiving our weekly newsletter with compliance tips, 
              industry updates, and exclusive resources.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 h-14 px-6 bg-white border-2 border-border rounded-full text-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                className="btn-primary whitespace-nowrap"
                data-testid="newsletter-submit"
              >
                Subscribe Free
              </button>
            </form>
            <p className="text-xs text-muted-foreground mt-4">
              No spam, unsubscribe anytime. Read our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding" data-testid="resources-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full" />
        </div>
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Need Personalized Guidance?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Our resources provide a great starting point, but every hospice is unique. 
              Schedule a free consultation to discuss your specific challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105 inline-flex items-center justify-center gap-2"
                data-testid="resources-cta-contact"
              >
                Book Free Consultation
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
              <Link
                to="/success-stories"
                className="border-2 border-white text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/10 inline-flex items-center justify-center gap-2"
                data-testid="resources-cta-stories"
              >
                View Success Stories
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;